/**
 * SurveyQuestionManager
 * Lets segment admins add, edit and delete survey questions for their segment.
 * Question types: Descriptive (text) | Multi Select (checkbox)
 */

import React, { useState, useEffect } from 'react';
import AdminApiService from '../services/AdminApiService';
import './SurveyQuestionManager.css';

const EMPTY_FORM = { questionText: '', questionType: 'text', options: [], isActive: true };

export default function SurveyQuestionManager() {
  const [questions, setQuestions]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [saving, setSaving]         = useState(false);

  // form state — null = hidden, 'new' = add form, number = edit id
  const [formMode, setFormMode]     = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [optionInput, setOptionInput] = useState('');
  const [formError, setFormError]   = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await AdminApiService.getSurveyQuestions();
      setQuestions(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── helpers ── */
  const typeLabel = (t) => t === 'text' ? 'Descriptive' : t === 'checkbox' ? 'Multi Select' : t;

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setOptionInput('');
    setFormError(null);
    setFormMode('new');
  };

  const openEdit = (q) => {
    setForm({
      questionText: q.questionText,
      questionType: q.questionType,
      options: Array.isArray(q.options) ? [...q.options] : [],
      isActive: q.isActive,
    });
    setOptionInput('');
    setFormError(null);
    setFormMode(q.id);
  };

  const cancelForm = () => { setFormMode(null); setFormError(null); };

  const addOption = () => {
    const v = optionInput.trim();
    if (!v) return;
    if (form.options.includes(v)) { setFormError('Option already exists'); return; }
    setForm(f => ({ ...f, options: [...f.options, v] }));
    setOptionInput('');
    setFormError(null);
  };

  const removeOption = (opt) =>
    setForm(f => ({ ...f, options: f.options.filter(o => o !== opt) }));

  const handleTypeChange = (type) => {
    setForm(f => ({ ...f, questionType: type, options: type === 'text' ? [] : f.options }));
    setFormError(null);
  };

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!form.questionText.trim()) { setFormError('Question text is required'); return; }
    if (form.questionType === 'checkbox' && form.options.length === 0) {
      setFormError('Add at least one option for Multi Select questions'); return;
    }

    setSaving(true);
    try {
      if (formMode === 'new') {
        await AdminApiService.createSurveyQuestion(
          form.questionText,
          form.questionType,
          form.questionType === 'checkbox' ? form.options : null
        );
      } else {
        await AdminApiService.updateSurveyQuestion(formMode, {
          questionText: form.questionText,
          questionType: form.questionType,
          options: form.questionType === 'checkbox' ? form.options : null,
          isActive: form.isActive,
        });
      }
      setFormMode(null);
      await load();
    } catch (e) {
      setFormError(e.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    try {
      await AdminApiService.deleteSurveyQuestion(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  /* ── render ── */
  if (loading) return <div className="sqm-loading">Loading questions…</div>;
  if (error)   return <div className="sqm-error">❌ {error}</div>;

  return (
    <div className="sqm-root">
      <div className="sqm-header">
        <h3>Survey Questions <span className="sqm-count">({questions.length})</span></h3>
        {formMode === null && (
          <button className="sqm-btn-add" onClick={openAdd}>+ Add Question</button>
        )}
      </div>

      {/* Add form */}
      {formMode === 'new' && (
        <QuestionForm
          form={form} setForm={setForm}
          optionInput={optionInput} setOptionInput={setOptionInput}
          formError={formError} saving={saving}
          onSubmit={handleSubmit} onCancel={cancelForm}
          onTypeChange={handleTypeChange}
          addOption={addOption} removeOption={removeOption}
          isNew
        />
      )}

      {/* Question list */}
      {questions.length === 0 && formMode === null && (
        <div className="sqm-empty">No questions yet. Click "+ Add Question" to create one.</div>
      )}

      <ol className="sqm-list">
        {questions.map((q) => (
          <li key={q.id} className={`sqm-item ${!q.isActive ? 'sqm-item--inactive' : ''}`}>
            {formMode === q.id ? (
              <QuestionForm
                form={form} setForm={setForm}
                optionInput={optionInput} setOptionInput={setOptionInput}
                formError={formError} saving={saving}
                onSubmit={handleSubmit} onCancel={cancelForm}
                onTypeChange={handleTypeChange}
                addOption={addOption} removeOption={removeOption}
              />
            ) : (
              <div className="sqm-item-body">
                <div className="sqm-item-left">
                  <span className="sqm-order">{q.questionOrder}.</span>
                  <div>
                    <div className="sqm-text">{q.questionText}</div>
                    <div className="sqm-meta">
                      <span className={`sqm-badge sqm-badge--${q.questionType}`}>{typeLabel(q.questionType)}</span>
                      {!q.isActive && <span className="sqm-badge sqm-badge--inactive">Inactive</span>}
                      {q.questionType !== 'text' && Array.isArray(q.options) && q.options.length > 0 && (
                        <span className="sqm-options-preview">
                          {q.options.map(o => <span key={o} className="sqm-chip">{o}</span>)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="sqm-item-actions">
                  <button className="sqm-btn-edit" onClick={() => openEdit(q)} title="Edit">✏️ Edit</button>
                  <button className="sqm-btn-delete" onClick={() => handleDelete(q.id)} title="Delete">🗑️ Delete</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ── Inline form sub-component ── */
function QuestionForm({
  form, setForm, optionInput, setOptionInput,
  formError, saving, onSubmit, onCancel, onTypeChange,
  addOption, removeOption, isNew = false,
}) {
  return (
    <form className="sqm-form" onSubmit={onSubmit}>
      <div className="sqm-form-title">{isNew ? 'New Question' : 'Edit Question'}</div>

      {formError && <div className="sqm-form-error">⚠ {formError}</div>}

      <div className="sqm-form-row">
        <label>Question text</label>
        <textarea
          className="sqm-textarea"
          rows={2}
          value={form.questionText}
          onChange={e => setForm(f => ({ ...f, questionText: e.target.value }))}
          placeholder="Enter your question…"
          required
        />
      </div>

      <div className="sqm-form-row">
        <label>Type</label>
        <div className="sqm-type-buttons">
          <button
            type="button"
            className={`sqm-type-btn ${form.questionType === 'text' ? 'active' : ''}`}
            onClick={() => onTypeChange('text')}
          >
            📝 Descriptive
          </button>
          <button
            type="button"
            className={`sqm-type-btn ${form.questionType === 'checkbox' ? 'active' : ''}`}
            onClick={() => onTypeChange('checkbox')}
          >
            ☑ Multi Select
          </button>
        </div>
      </div>

      {form.questionType === 'checkbox' && (
        <div className="sqm-form-row">
          <label>Options</label>
          <div className="sqm-options-builder">
            <div className="sqm-options-chips">
              {form.options.map(opt => (
                <span key={opt} className="sqm-chip sqm-chip--removable">
                  {opt}
                  <button type="button" className="sqm-chip-remove" onClick={() => removeOption(opt)}>×</button>
                </span>
              ))}
              {form.options.length === 0 && <span className="sqm-options-empty">No options yet</span>}
            </div>
            <div className="sqm-option-adder">
              <input
                className="sqm-option-input"
                value={optionInput}
                onChange={e => setOptionInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                placeholder="Type an option and press Enter or Add"
              />
              <button type="button" className="sqm-btn-add-option" onClick={addOption}>Add</button>
            </div>
          </div>
        </div>
      )}

      {!isNew && (
        <div className="sqm-form-row sqm-form-row--inline">
          <label>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
            />
            {' '}Active (shown to users)
          </label>
        </div>
      )}

      <div className="sqm-form-actions">
        <button type="button" className="sqm-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="sqm-btn-save" disabled={saving}>
          {saving ? 'Saving…' : 'Save Question'}
        </button>
      </div>
    </form>
  );
}
